// src/pages/dataInput/utils/ddlParser.ts
// ─────────────────────────────────────────────────────────────────────────────
// Lightweight DDL parser. Goal: extract a usable JSON skeleton from one or more
// CREATE TABLE statements. Handles common dialects (Postgres, MySQL, SQL Server,
// Oracle). Definitely not a full SQL parser — the user gets a preview to fix
// anything we got wrong before submitting.
// ─────────────────────────────────────────────────────────────────────────────

export interface ParsedColumn {
  name:        string;
  data_type:   string;
  length?:     number | null;
  precision?:  number | null;
  scale?:      number | null;
  nullable:    boolean;
  is_pk:       boolean;
  description: string;
}

export interface ParsedRelationship {
  from_table:  string;
  from_column: string;
  to_table:    string;
  to_column:   string;
}

export interface ParsedTable {
  table_name:  string;
  description: string;
  columns:     ParsedColumn[];
}

export interface ParsedDDL {
  tables:        ParsedTable[];
  relationships: ParsedRelationship[];
  warnings:      string[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const stripComments = (sql: string): string =>
  sql
    // -- line comments
    .replace(/--[^\n]*/g, "")
    // /* block comments */
    .replace(/\/\*[\s\S]*?\*\//g, "");

const unquoteIdent = (raw: string): string =>
  raw
    .trim()
    .replace(/^"(.+)"$/, "$1")
    .replace(/^\[(.+)\]$/, "$1")
    .replace(/^`(.+)`$/, "$1");

/** Split a list at top-level commas (commas not inside parentheses). */
function splitTopLevel(s: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let buf = "";
  for (const c of s) {
    if (c === "(") depth++;
    else if (c === ")") depth--;
    if (c === "," && depth === 0) {
      out.push(buf.trim());
      buf = "";
    } else {
      buf += c;
    }
  }
  if (buf.trim()) out.push(buf.trim());
  return out;
}

// Match CREATE TABLE [IF NOT EXISTS] [schema.]name ( body );
const CREATE_TABLE_RE =
  /create\s+table\s+(?:if\s+not\s+exists\s+)?([^\s(]+)\s*\(([\s\S]*?)\)\s*(?:[a-z_]+\s*=\s*[^;]+|\bwith[\s\S]*?|\b[a-z][\w]*[\s\S]*?)?;?/gi;

// Inline column-level: ... PRIMARY KEY ... or ... NOT NULL ... or ... DEFAULT ...
const INLINE_PK_RE       = /\bprimary\s+key\b/i;
const INLINE_NOTNULL_RE  = /\bnot\s+null\b/i;
const INLINE_NULL_RE     = /\bnull\b/i;
const INLINE_COMMENT_RE  = /comment\s+'([^']*)'/i;

// Table-level constraint detectors
const TBL_PK_RE = /^constraint\s+\S+\s+primary\s+key\s*\(([^)]+)\)|^primary\s+key\s*\(([^)]+)\)/i;
const TBL_FK_RE =
  /^(?:constraint\s+\S+\s+)?foreign\s+key\s*\(([^)]+)\)\s*references\s+([^\s(]+)\s*\(([^)]+)\)/i;

// Column data type extractor:  TYPE [(n[,m])]
const TYPE_RE = /^([a-z_][\w]*(?:\s+[a-z_][\w]*)?)(?:\s*\(\s*(\d+)(?:\s*,\s*(\d+))?\s*\))?/i;

// ── Parser ───────────────────────────────────────────────────────────────────

export function parseDDL(sql: string): ParsedDDL {
  const cleaned = stripComments(sql);
  const tables: ParsedTable[] = [];
  const relationships: ParsedRelationship[] = [];
  const warnings: string[] = [];

  let m: RegExpExecArray | null;
  CREATE_TABLE_RE.lastIndex = 0;

  while ((m = CREATE_TABLE_RE.exec(cleaned)) !== null) {
    const rawName = m[1];
    const body    = m[2];
    if (!rawName || !body) continue;

    // table_name (drop schema prefix)
    const nameParts = rawName.split(".");
    const tableName = unquoteIdent(nameParts[nameParts.length - 1]);

    const items = splitTopLevel(body);
    const columns: ParsedColumn[] = [];
    const tablePKs: string[] = [];

    for (const item of items) {
      const trimmed = item.trim();

      // Table-level PRIMARY KEY (...)
      const pkMatch = trimmed.match(TBL_PK_RE);
      if (pkMatch) {
        const cols = (pkMatch[1] || pkMatch[2]).split(",").map(c => unquoteIdent(c));
        tablePKs.push(...cols);
        continue;
      }

      // Table-level FOREIGN KEY (...) REFERENCES other(...)
      const fkMatch = trimmed.match(TBL_FK_RE);
      if (fkMatch) {
        const fromCols = fkMatch[1].split(",").map(c => unquoteIdent(c));
        const toTableRaw = fkMatch[2];
        const toCols   = fkMatch[3].split(",").map(c => unquoteIdent(c));
        const toTable  = unquoteIdent(toTableRaw.split(".").pop()!);
        for (let i = 0; i < fromCols.length && i < toCols.length; i++) {
          relationships.push({
            from_table:  tableName,
            from_column: fromCols[i],
            to_table:    toTable,
            to_column:   toCols[i],
          });
        }
        continue;
      }

      // Skip other CONSTRAINT lines we don't model
      if (/^constraint\b/i.test(trimmed) || /^check\s*\(/i.test(trimmed) ||
          /^unique\s*\(/i.test(trimmed)  || /^index\b/i.test(trimmed)) {
        continue;
      }

      // ── Column definition ────────────────────────────────────────────────
      // First token = column name (possibly quoted)
      const colTok = trimmed.match(/^("([^"]+)"|`([^`]+)`|\[([^\]]+)\]|([a-zA-Z_][\w]*))(\s+|$)/);
      if (!colTok) {
        warnings.push(`Could not parse line: ${trimmed.slice(0, 80)}`);
        continue;
      }
      const colName = unquoteIdent(colTok[1]);
      const rest    = trimmed.slice(colTok[0].length).trim();

      // Type
      const typeMatch = rest.match(TYPE_RE);
      let dataType  = "TEXT";
      let length: number | null    = null;
      let precision: number | null = null;
      let scale: number | null     = null;

      if (typeMatch) {
        dataType = typeMatch[1].toUpperCase();
        if (typeMatch[2] !== undefined && typeMatch[3] !== undefined) {
          precision = parseInt(typeMatch[2], 10);
          scale     = parseInt(typeMatch[3], 10);
        } else if (typeMatch[2] !== undefined) {
          // Could be length OR single-arg precision; treat as length for char types
          const isCharType = /char|text|string|varchar|nvarchar|nchar/i.test(dataType);
          if (isCharType) length = parseInt(typeMatch[2], 10);
          else            precision = parseInt(typeMatch[2], 10);
        }
      }

      const isPK     = INLINE_PK_RE.test(rest);
      const isNotNull = INLINE_NOTNULL_RE.test(rest);
      const cmtMatch  = rest.match(INLINE_COMMENT_RE);
      const description = cmtMatch ? cmtMatch[1] : "";

      columns.push({
        name:        colName,
        data_type:   dataType,
        length,
        precision,
        scale,
        nullable:    !isNotNull,
        is_pk:       isPK,
        description,
      });
    }

    // Apply table-level PKs
    for (const pkCol of tablePKs) {
      const c = columns.find(cc => cc.name === pkCol);
      if (c) c.is_pk = true;
    }

    if (columns.length === 0) {
      warnings.push(`Table '${tableName}' had no parseable columns.`);
      continue;
    }

    tables.push({
      table_name:  tableName,
      description: "",
      columns,
    });
  }

  if (tables.length === 0) {
    warnings.push("No CREATE TABLE statements found in the file.");
  }

  return { tables, relationships, warnings };
}

// Convenience: parse multiple files (concatenates them)
export async function parseDDLFiles(files: File[]): Promise<ParsedDDL> {
  const texts = await Promise.all(files.map(f => f.text()));
  return parseDDL(texts.join("\n;\n"));
}